import { useEffect, useState } from "react";
import api, { apiError, setToken } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function AdminAccount() {
  const { admin, refresh, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [emailForm, setEmailForm] = useState({ email: "", current_password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (admin) setEmailForm((f) => ({ ...f, email: admin.email }));
  }, [admin]);

  const changeEmail = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/change-email", emailForm);
      setToken(data.token);
      await refresh();
      toast.success("Email updated");
      setEmailForm((f) => ({ ...f, current_password: "" }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated. Please sign in again.");
      logout();
      navigate("/admin/login", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="admin-account">
      <header className="mb-8">
        <span className="font-script text-3xl text-blush-400">your account</span>
        <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Account</h1>
      </header>

      <section className="rounded-3xl bg-white border border-blush-100 p-8 mb-6">
        <h2 className="font-heading text-xl text-ink-900">Change email</h2>
        <form onSubmit={changeEmail} className="mt-5 grid gap-4">
          <div>
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">New email</label>
            <input type="email" required value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} className="field mt-2" data-testid="account-new-email" />
          </div>
          <div>
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Current password</label>
            <input type="password" required value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} className="field mt-2" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="btn-maroon disabled:opacity-60" data-testid="account-save-email">Update email</button>
          </div>
        </form>
      </section>

      <section className="rounded-3xl bg-white border border-blush-100 p-8">
        <h2 className="font-heading text-xl text-ink-900">Change password</h2>
        <form onSubmit={changePw} className="mt-5 grid gap-4">
          <div>
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Current password</label>
            <input type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="field mt-2" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">New password</label>
              <input type="password" required minLength={8} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="field mt-2" data-testid="account-new-pw" />
              <p className="mt-1 text-[11px] font-body text-ink-500">Minimum 8 characters.</p>
            </div>
            <div>
              <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Confirm new password</label>
              <input type="password" required minLength={8} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="field mt-2" />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="btn-maroon disabled:opacity-60" data-testid="account-save-pw">Update password</button>
          </div>
        </form>
      </section>
    </div>
  );
}
